// The chat request's `voice` field (BUILD 2 C2-7), and the user message a
// voice turn sends. app.tsx merges `currentVoiceTurnBody()` into the transport
// body next to `viewport` on every send, so only a voice-origin turn carries it.
//
// The voice controller arms the body immediately before it appends the turn
// and disarms it once the turn is running, when voice mode leaves, or after
// TURN_BODY_TTL_MS. A read does not consume it: the server fails a turn whose
// `voice` is invalid (400) rather than run it as a typed turn, and the body
// must reach the one request it was armed for even if another read (a stream
// reconnect) comes first. Nothing typed can be sent while it is armed: the
// voice bar holds the composer until the controller disarms.
//
// This is one of the two places an utterance's text is read (`toChatText()`,
// pinned by test/voice-no-keyword-routing.mjs): it becomes the ordinary user
// message, beside a display-only `data-speech` part (C2-7 item 6) the model
// never reads.
import type { CreateAppendMessage } from '@assistant-ui/react';
import {
  VOICE_ORIGIN_DATA_TYPE,
  type PlaybackStop,
  type Utterance,
  type VoiceOriginData,
  type VoiceStyle,
  type VoiceTurnBody,
} from './contracts';
import { TURN_BODY_TTL_MS } from './policy';

export type VoiceTurnContext = {
  style: VoiceStyle;
  /** The server session the utterance was committed in. */
  sessionId: string;
  provider: string;
  degraded: boolean;
  /** Sent by the undo countdown, not by Send now or a tap. */
  autoSent: boolean;
  /** The undo window the turn waited (0 when sent at once). */
  undoMs: number;
  /** The reply will be read aloud by Poneva's voice. */
  readAloud: boolean;
  /** Where the person stopped the previous reply, if they did. */
  interrupted: PlaybackStop | null;
  /** The previous reply's first audio, in ms from its send. */
  firstAudioMs: number | null;
  /** This client can play audio (read-aloud is available). */
  playback: boolean;
};

const clampInt = (value: number, max: number) =>
  Math.max(0, Math.min(max, Math.round(Number.isFinite(value) ? value : 0)));
const bounded = (value: string, max: number) => value.trim().slice(0, max);

/** The C2-7 body for a turn (pure). Fields are bounded as the server's schema requires. */
export function voiceTurnBody(utterance: Utterance, context: VoiceTurnContext): VoiceTurnBody {
  const body: VoiceTurnBody = {
    commit: utterance.commit,
    style: context.style,
    sessionId: bounded(context.sessionId, 64),
    provider: bounded(context.provider, 64) || 'unknown',
    degraded: context.degraded,
    autoSent: context.autoSent,
    undoMs: clampInt(context.undoMs, 10_000),
    readAloud: context.readAloud,
    capabilities: { control: true, playback: context.playback },
  };
  if (utterance.lang) body.lang = utterance.lang;
  if (context.interrupted?.messageId)
    body.interrupted = {
      messageId: bounded(context.interrupted.messageId, 128),
      heardChars: clampInt(context.interrupted.charOffset, 1_000_000),
      ...(context.interrupted.playback
        ? {
            playback: {
              id: context.interrupted.playback.id,
              playedMs: clampInt(context.interrupted.playback.playedMs, 600_000),
            },
          }
        : {}),
    };
  if (context.firstAudioMs !== null) body.firstAudioMs = clampInt(context.firstAudioMs, 600_000);
  return body;
}

/** The user message for a voice turn: the text, and the display-only voice-origin part. */
export function voiceTurnMessage(utterance: Utterance, body: VoiceTurnBody): CreateAppendMessage {
  const origin: VoiceOriginData = {
    via: 'voice',
    commit: body.commit,
    autoSent: body.autoSent,
    provider: body.provider,
    ...(body.interrupted ? { interrupted: body.interrupted } : {}),
  };
  return {
    role: 'user',
    content: [
      { type: 'text', text: utterance.toChatText() },
      { type: 'data', name: VOICE_ORIGIN_DATA_TYPE.slice('data-'.length), data: origin },
    ],
  };
}

type Armed = { body: VoiceTurnBody; expiresAt: number };
let armed: Armed | null = null;
const clock = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

/** Arm the body for the send that follows. */
export function armVoiceTurn(body: VoiceTurnBody, now: number = clock()): void {
  armed = { body, expiresAt: now + TURN_BODY_TTL_MS };
}

/** The turn is running, failed, or voice mode left: typed turns carry no body. */
export function disarmVoiceTurn(): void {
  armed = null;
}

/** The voice body for the turn being sent, or null for a typed turn. */
export function currentVoiceTurnBody(now: number = clock()): VoiceTurnBody | null {
  if (armed && now > armed.expiresAt) armed = null;
  return armed?.body ?? null;
}
